import * as React from "react";
import { Check, Minus } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const tiers = [{
  id: "bronze",
  name: "Bronze Snapshot",
  price: "Free – £1,145",
  priceSubtext: "*by org size",
  turnaround: "1-2 weeks",
  cta: "Get Started",
  ctaVariant: "secondary" as const,
  highlight: false
}, {
  id: "silver",
  name: "Silver Insight",
  price: "£250 – £7,500",
  priceSubtext: "",
  turnaround: "2-3 weeks",
  cta: "Request Demo",
  ctaVariant: "default" as const,
  highlight: true,
  badge: "Most Popular"
}, {
  id: "gold",
  name: "Gold Action Plan",
  price: "£450 – £13,500",
  priceSubtext: "",
  turnaround: "3-4 weeks",
  cta: "Request Demo",
  ctaVariant: "default" as const,
  highlight: false
}, {
  id: "platinum",
  name: "Platinum Deep Diagnostic",
  price: "£650 – £18,900",
  priceSubtext: "",
  turnaround: "4-6 weeks",
  cta: "Get Started",
  ctaVariant: "secondary" as const,
  highlight: false
}];
const features = [{
  name: "Core Wellbeing Metrics",
  values: [true, true, true, true]
}, {
  name: "Department Benchmarking",
  values: [false, true, true, true]
}, {
  name: "Custom Intervention Plan",
  values: [false, false, true, true]
}, {
  name: "Executive Presentation",
  values: [false, false, false, true]
}, {
  name: "Ongoing Support",
  values: [false, false, false, true]
}];
export default function PricingPackages() {
  return <section id="pricing-packages" className="w-full py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 max-w-6xl">
        <div className="mb-12 text-center">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            Choose Your Path to Better Wellbeing
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-[15px]">
            Select the package that best fits your organization's size and needs. 
            From a quick snapshot to a deep diagnostic, we have you covered.
          </p>
        </div>

        <div className="relative w-full overflow-hidden rounded-xl border border-border bg-card shadow-sm">
          <Table className="min-w-[800px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent border-b-0">
                <TableHead className="w-[200px] bg-secondary border-r border-secondary-foreground/10"></TableHead>
                {tiers.map((tier, i) => <TableHead key={tier.id} data-index={i} className={`h-auto py-6 px-4 text-center align-top bg-secondary text-secondary-foreground ${tier.highlight ? "border-t-4 border-x-4 border-primary relative z-10" : "border-r border-secondary-foreground/10 last:border-r-0"}`}>
                    <div className="flex flex-col items-center justify-start gap-3 h-full">
                      {tier.highlight ? <Badge className="bg-primary text-primary-foreground hover:bg-primary border-none font-bold uppercase tracking-wider text-[10px] px-3 py-1">
                          {tier.badge}
                        </Badge> : <div className="h-[26px]" aria-hidden="true"></div>}
                      <span className="font-default font-bold text-lg leading-tight">
                        {tier.name}
                      </span>
                    </div>
                  </TableHead>)}
              </TableRow>
            </TableHeader>
            <TableBody>
              {}
              <TableRow className="bg-background hover:bg-background">
                <TableCell className="font-default font-bold text-foreground text-[15px] p-4 border-r border-border">
                  Price Range
                </TableCell>
                {tiers.map((tier, i) => <TableCell key={`price-${tier.id}`} data-index={i} className={`text-center p-4 text-[15px] text-foreground font-medium ${tier.highlight ? "border-x-4 border-primary bg-primary/5 relative z-10" : "border-r border-border last:border-r-0"}`}>
                    {tier.price}
                    {tier.priceSubtext && <span className="block text-xs text-muted-foreground mt-1 font-normal">
                        {tier.priceSubtext}
                      </span>}
                  </TableCell>)}
              </TableRow>

              {}
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                <TableCell className="font-default font-bold text-foreground text-[15px] p-4 border-r border-border">
                  Est. Turnaround
                </TableCell>
                {tiers.map((tier, i) => <TableCell key={`turnaround-${tier.id}`} data-index={i} className={`text-center p-4 text-[15px] text-foreground ${tier.highlight ? "border-x-4 border-primary bg-primary/5 relative z-10" : "border-r border-border last:border-r-0"}`}>
                    {tier.turnaround}
                  </TableCell>)}
              </TableRow>

              {}
              {features.map((feature, rowIndex) => <TableRow key={rowIndex} data-index={rowIndex} className={`hover:bg-transparent ${rowIndex % 2 === 0 ? "bg-background" : "bg-muted/30"}`}>
                  <TableCell className="font-default font-bold text-foreground text-[15px] p-4 border-r border-border">
                    {feature.name}
                  </TableCell>
                  {feature.values.map((val, colIndex) => <TableCell key={`${rowIndex}-${colIndex}`} data-index={colIndex} className={`text-center p-4 ${tiers[colIndex].highlight ? "border-x-4 border-primary bg-primary/5 relative z-10" : "border-r border-border last:border-r-0"}`}>
                      {val ? <Check className="mx-auto size-5 text-primary" /> : <Minus className="mx-auto size-5 text-muted-foreground/30" />}
                    </TableCell>)}
                </TableRow>)}

              {}
              <TableRow className="bg-background hover:bg-background border-b-0">
                <TableCell className="border-r border-border p-4"></TableCell>
                {tiers.map((tier, i) => <TableCell key={`cta-${tier.id}`} data-index={i} className={`text-center p-6 align-bottom ${tier.highlight ? "border-b-4 border-x-4 border-primary bg-primary/5 relative z-10 rounded-b-lg" : "border-r border-border last:border-r-0"}`}>
                    <Link to="#" className="inline-block w-full">
                      <Button variant={tier.ctaVariant} className="w-full rounded-full font-bold shadow-md hover:shadow-lg transition-all">
                        {tier.cta}
                      </Button>
                    </Link>
                  </TableCell>)}
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </div>
    </section>;
}